import React, { useEffect, useRef, useState } from 'react';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import throttle from 'lodash/throttle';
import { isNil } from 'ramda';
import { SpacingPropsType } from 'types/Layout';
import { useDeviceLayout } from '../../../../context/deviceLayout';
import { cssTheme } from '../../../../themes';
import { Button } from '../../../designSystem/Button';
import { Icon } from '../../../designSystem/Icon';
const THROTTLING_INTERVAL = 100; // ms

interface Props {
  /** Elements that will be rendered inside the carousel */
  children: React.ReactNode;
  /** Whether the language is RTL or not */
  isRtlLanguage?: boolean;
  /** Custom prop to hide nav buttons only on mobileSm resolution (false by default) */
  hideNavButtonsOnlyForMobileSm?: boolean;
  /** A function to run when scroll happens */
  onScroll?: () => void;
  hasSidePaddings?: boolean;
  itemsGap?: SpacingPropsType;
}
const _exp2 = /*#__PURE__*/() => ({
  previousButtonVisibility
}) => previousButtonVisibility ? 0 : 1;
const _exp3 = /*#__PURE__*/() => ({
  nextButtonVisibility
}) => nextButtonVisibility ? 0 : 1;
const _exp4 = /*#__PURE__*/() => ({
  itemsGap
}) => cssTheme.layout.spacing[itemsGap];
const ScrollWrapper = /*#__PURE__*/styled('div')({
  name: "ScrollWrapper",
  class: "s6663bx",
  propsAsIs: false,
  vars: {
    "s6663bx-0": [_exp2()],
    "s6663bx-1": [_exp3()],
    "s6663bx-2": [_exp4()]
  }
});
const CardsAndNavButtonsWrapper = /*#__PURE__*/styled('div')({
  name: "CardsAndNavButtonsWrapper",
  class: "cy5fihb",
  propsAsIs: false
});
const NavigateButtonWrapper = /*#__PURE__*/styled('div')({
  name: "NavigateButtonWrapper",
  class: "n169jphi",
  propsAsIs: false
});
export const SmoothScrollNavigation = ({
  children,
  isRtlLanguage = false,
  hideNavButtonsOnlyForMobileSm,
  onScroll,
  hasSidePaddings,
  itemsGap = 's500'
}: Props) => {
  const {
    isMobile,
    isMobileSm
  } = useDeviceLayout();
  const [cardsPerClick, setCardsPerClick] = useState(2);
  const [cardWidth, setCardWidth] = useState(0);
  const [scrollPosition, setScrollPosition] = useState(0);
  const scrollWrapperRef = useRef<HTMLDivElement>(null);
  const [navButtonVisibility, setNavButtonVisibility] = useState({
    next: false,
    prev: false
  });
  useEffect(() => {
    const ref = scrollWrapperRef.current;
    if (!ref) {
      return;
    }
    const throttledSetScrollPosition = throttle(() => {
      setScrollPosition(Math.round(ref.scrollLeft));
    }, THROTTLING_INTERVAL);
    const handleScroll = () => {
      throttledSetScrollPosition();
      onScroll?.();
    };
    ref.addEventListener('scroll', handleScroll);
    return () => ref.removeEventListener('scroll', handleScroll);
  }, []);
  const container = scrollWrapperRef.current;
  const {
    clientWidth,
    scrollWidth,
    scrollLeft: containerScrollLeft
  } = container || {};
  const scrollLeft = isRtlLanguage && containerScrollLeft ? Math.abs(containerScrollLeft) : containerScrollLeft;
  useEffect(() => {
    const isHideNavButtons = hideNavButtonsOnlyForMobileSm ? isMobileSm : isMobile;
    if (isHideNavButtons) {
      setNavButtonVisibility({
        next: false,
        prev: false
      });
      return;
    }
    if (!isNil(scrollLeft) && !isNil(clientWidth) && !isNil(scrollWidth)) {
      const thresholdFromSides = 15;
      const nextButtonVisibility = scrollLeft + clientWidth < scrollWidth - thresholdFromSides;
      const prevButtonVisibility = scrollLeft > thresholdFromSides;
      setNavButtonVisibility({
        next: nextButtonVisibility,
        prev: prevButtonVisibility
      });
    }
  }, [scrollPosition, scrollWrapperRef, isMobile, isRtlLanguage, isMobileSm, hideNavButtonsOnlyForMobileSm, container, scrollLeft, clientWidth, scrollWidth]);
  useEffect(() => {
    const container = scrollWrapperRef.current;
    if (!container) {
      return;
    }
    const {
      firstElementChild
    } = container;
    if (!firstElementChild) {
      return;
    }
    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        setCardWidth(entry.target.getBoundingClientRect().width);
        setCardsPerClick(Math.floor(container.clientWidth / entry.target.getBoundingClientRect().width) - 1);
      }
    });
    resizeObserver.observe(firstElementChild);
    return () => {
      resizeObserver.disconnect();
    };
  }, [children]);
  const handleNavigateButtonClick = (direction: 'next' | 'prev') => () => {
    const container = scrollWrapperRef.current;
    if (!container) {
      return;
    }
    const scrollPerClick = cardsPerClick * cardWidth;
    const scrollOffset = direction === 'next' ? -scrollPerClick : scrollPerClick;
    const widthToScroll = isRtlLanguage ? container.scrollLeft + scrollOffset : container.scrollLeft - scrollOffset;
    container.scroll({
      left: widthToScroll,
      behavior: 'smooth'
    });
  };
  return <CardsAndNavButtonsWrapper>
      {navButtonVisibility?.prev && <NavigateButtonWrapper className="direction-prev">
          <Button variant="secondary" isFloating isRounded size="sm" iconStart={<Icon name="ChevronLeft" />} onClick={handleNavigateButtonClick('prev')} />
        </NavigateButtonWrapper>}
      <ScrollWrapper nextButtonVisibility={navButtonVisibility?.next} previousButtonVisibility={navButtonVisibility?.prev} itemsGap={itemsGap} ref={scrollWrapperRef} className={cx(hasSidePaddings && 'hasSidePaddings')}>
        {children}
      </ScrollWrapper>

      {navButtonVisibility?.next && <NavigateButtonWrapper className="direction-next">
          <Button variant="secondary" isFloating isRounded size="sm" iconStart={<Icon name="ChevronRight" />} onClick={handleNavigateButtonClick('next')} />
        </NavigateButtonWrapper>}
    </CardsAndNavButtonsWrapper>;
};

require("./SmoothScrollNavigation.wyw-in-js.css!=!../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./SmoothScrollNavigation.tsx");